/**
 * TextAnnotationManager
 * 
 * Separate class for managing text annotations on images.
 * This can be easily removed if text functionality is not needed.
 */
export interface TextElement {
  id: string;
  x: number;
  y: number;
  text: string;
  color: string;
  fontSize: number;
}

export class TextAnnotationManager {
  private textElements: TextElement[] = [];
  private textCanvas: HTMLCanvasElement | null = null;
  private imageElement: HTMLImageElement | null = null;
  private canvasSize: { width: number; height: number } = { width: 0, height: 0 };

  /**
   * Initialize the text annotation manager
   */
  initialize(
    textCanvas: HTMLCanvasElement,
    imageElement: HTMLImageElement,
    canvasSize: { width: number; height: number }
  ) {
    this.textCanvas = textCanvas;
    this.imageElement = imageElement;
    this.canvasSize = canvasSize;
    this.updateCanvasSize();
  }

  /**
   * Update canvas size
   */
  updateCanvasSize(canvasSize?: { width: number; height: number }) {
    if (canvasSize) {
      this.canvasSize = canvasSize;
    }
    if (this.textCanvas) {
      this.textCanvas.width = this.canvasSize.width;
      this.textCanvas.height = this.canvasSize.height;
      this.redraw();
    }
  }

  /**
   * Add a text element
   */
  addText(x: number, y: number, text: string, color: string, fontSize: number): TextElement {
    const newText: TextElement = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      x,
      y,
      text: text.trim(),
      color,
      fontSize
    };
    this.textElements.push(newText);
    this.redraw();
    return newText;
  }

  /**
   * Remove a text element
   */
  removeText(id: string): boolean {
    const index = this.textElements.findIndex(t => t.id === id);
    if (index !== -1) {
      this.textElements.splice(index, 1);
      this.redraw();
      return true;
    }
    return false;
  }

  /**
   * Get all text elements
   */
  getTextElements(): TextElement[] {
    return [...this.textElements];
  }

  /**
   * Clear all text elements
   */
  clear(): void {
    this.textElements = [];
    this.redraw();
  }

  /**
   * Redraw text canvas
   */
  private redraw(): void {
    if (!this.textCanvas) return;

    const ctx = this.textCanvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, this.textCanvas.width, this.textCanvas.height);

    this.textElements.forEach(textEl => {
      ctx.font = `${textEl.fontSize}px Arial`;
      ctx.fillStyle = textEl.color;
      ctx.fillText(textEl.text, textEl.x, textEl.y);
    });
  }

  /**
   * Draw text elements on a canvas context (for saving)
   */
  drawOnCanvas(
    ctx: CanvasRenderingContext2D,
    scaleX: number,
    scaleY: number
  ): void {
    this.textElements.forEach(textEl => {
      ctx.font = `${textEl.fontSize * scaleY}px Arial`;
      ctx.fillStyle = textEl.color;
      ctx.fillText(textEl.text, textEl.x * scaleX, textEl.y * scaleY);
    });
  }

  /**
   * Get coordinates relative to the image
   */
  getImageCoordinates(
    clientX: number,
    clientY: number,
    imageRect: DOMRect
  ): { x: number; y: number } {
    return {
      x: clientX - imageRect.left,
      y: clientY - imageRect.top
    };
  }
}

